class Slot {
  constructor(start, end) {
    this.start = start;
    this.end = end;
  }
  // Parse mixed Node|string items into Node[] within the context of this slot's
  // document. This is a private helper that uses the slot's start comment to
  // resolve the ownerDocument. mode: 'html'|'text'|'auto'
  parseItems(items, mode = "html") {
    const out = [];
    const host = this.start;
    const doc = host && host.ownerDocument ? host.ownerDocument : typeof document !== "undefined" ? document : void 0;
    if (!doc) return out;
    for (const it of items) {
      if (typeof it === "string") {
        if (mode === "text") {
          out.push(doc.createTextNode(it));
        } else {
          const template = doc.createElement("template");
          template.innerHTML = it;
          const childNodes = Array.from(template.content.childNodes);
          out.push(...childNodes);
        }
      } else {
        out.push(it);
      }
    }
    return out;
  }
  // Return nodes between start and end (exclusive) using nextSibling traversal
  nodes() {
    const out = [];
    let cur = this.start.nextSibling;
    while (cur && cur !== this.end) {
      out.push(cur);
      cur = cur.nextSibling;
    }
    return out;
  }
  append(...items) {
    if (!items || items.length === 0) return;
    const nodesToInsert = this.parseItems(items);
    if (nodesToInsert.length === 0) return;
    const parent = this.end ? this.end.parentNode : this.start.parentNode;
    const ref = this.end || null;
    for (const n of nodesToInsert) {
      if (ref) parent.insertBefore(n, ref);
      else parent.appendChild(n);
    }
  }
  // Explicit HTML/text variants
  appendHtml(...items) {
    this.append(...items);
  }
  appendText(...items) {
    const nodesToInsert = this.parseItems(items, "text");
    if (!nodesToInsert || nodesToInsert.length === 0) return;
    this.append(...nodesToInsert);
  }
  prepend(...items) {
    if (!items || items.length === 0) return;
    const nodesToInsert = this.parseItems(items);
    if (nodesToInsert.length === 0) return;
    const parent = this.start.parentNode;
    const before = this.start.nextSibling;
    for (let i = nodesToInsert.length - 1; i >= 0; i--) {
      const n = nodesToInsert[i];
      parent.insertBefore(n, before);
    }
  }
  prependHtml(...items) {
    this.prepend(...items);
  }
  prependText(...items) {
    const nodesToInsert = this.parseItems(items, "text");
    if (!nodesToInsert || nodesToInsert.length === 0) return;
    this.prepend(...nodesToInsert);
  }
  clear() {
    let cur = this.start.nextSibling;
    while (cur && cur !== this.end) {
      const next = cur.nextSibling;
      if ("remove" in cur && typeof cur.remove === "function")
        cur.remove();
      cur = next;
    }
  }
  // Replace all children between start and end with provided items.
  // Items can be Node or string (HTML/text). Uses parseItems to create
  // insertion nodes and then appends them (after clearing existing children).
  replaceChildren(...items) {
    this.clear();
    if (!items || items.length === 0) return;
    const nodesToInsert = this.parseItems(items);
    if (!nodesToInsert || nodesToInsert.length === 0) return;
    this.append(...nodesToInsert);
  }
  /**
   * Explicit HTML variant of replaceChildren — alias for replaceChildren
   * to make intent clear.
   */
  replaceChildrenHtml(...items) {
    this.replaceChildren(...items);
  }
  /**
   * Explicit text variant of replaceChildren — parses strings as text nodes
   * and replaces the children with those text nodes.
   */
  replaceChildrenText(...items) {
    this.clear();
    if (!items || items.length === 0) return;
    const nodesToInsert = this.parseItems(items, "text");
    if (!nodesToInsert || nodesToInsert.length === 0) return;
    this.append(...nodesToInsert);
  }
  findAll(predicate) {
    if (!predicate) return this.nodes();
    return this.nodes().filter(predicate);
  }
  querySelector(selector) {
    if (!selector) return null;
    for (const n of this.nodes()) {
      if (n.nodeType === Node.ELEMENT_NODE) {
        const el = n;
        if (el.matches && el.matches(selector)) return el;
        const found = el.querySelector(selector);
        if (found) return found;
      }
    }
    return null;
  }
  querySelectorAll(selector) {
    if (!selector) return [];
    const out = [];
    for (const n of this.nodes()) {
      if (n.nodeType === Node.ELEMENT_NODE) {
        const el = n;
        if (el.matches && el.matches(selector)) out.push(el);
        const found = el.querySelectorAll(selector);
        if (found && found.length) out.push(...Array.from(found));
      }
    }
    return out;
  }
  /**
   * Append clones of provided items into this slot.
   * Strings will be parsed and resulting nodes cloned; Node items will be cloned.
   */
  appendCopy(...items) {
    const parsed = this.parseItems(items);
    if (!parsed || parsed.length === 0) return;
    const clones = parsed.map((n) => n.cloneNode(true));
    if (clones.length === 0) return;
    this.append(...clones);
  }
  /**
   * Prepend clones of provided items into this slot.
   */
  prependCopy(...items) {
    const parsed = this.parseItems(items);
    if (!parsed || parsed.length === 0) return;
    const clones = parsed.map((n) => n.cloneNode(true));
    if (clones.length === 0) return;
    this.prepend(...clones);
  }
}
export {
  Slot
};
//# sourceMappingURL=Slot.js.map
